import { notFound, useRouterState } from '@tanstack/react-router'
import { useSettings } from '../../components/UI/settings-context'
import LayoutFirstBigAllImage from '../../components/Publications/LayoutFirstBigAllImage'
import LayoutAllSameSize from '../../components/Publications/LayoutAllSameSize'
import LayoutFirstBigNoImage from '../../components/Publications/LayoutFirstBigNoImage'
import LayoutVerticalNoImage from '../../components/Publications/LayoutVerticalNoImage'
import LayoutGallery from '../../components/Publications/LayoutGallery'
import LayoutBrissago from '../../components/Publications/LayoutBrissago'
import LayoutWzTv from '../../components/Publications/LayoutWzTv'
import { Container } from '@chakra-ui/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getPublications } from '../../api/publications'
import { useEffect, useState } from 'react'
import { PublicationPublicPreview } from '../../client'


export const layoutMap = {
  'all-same-size': LayoutAllSameSize,
  'first-big-no-img': LayoutFirstBigNoImage,
  'first-big-all-img': LayoutFirstBigAllImage,
  'vertical-no-img': LayoutVerticalNoImage,
  'brissago': LayoutBrissago,
  'gallery': LayoutGallery,
  'wz-tv': LayoutWzTv,
} as const


const CategoryPublications = () => {
    const queryClient = useQueryClient()
    const navigationPoints = useSettings()?.navigationPoints
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    })

    const cleanPath = pathname.replace('/', '').toLowerCase()

    const currentNav = navigationPoints?.data?.flatMap(parent => {
        if (parent.path === cleanPath) return parent

        const child = parent.children?.find(c => c.path === cleanPath)
        if (child) return child

        return []
    })[0]

    if (!currentNav) throw notFound()

    const layout = (currentNav.custom_view ?? 'first-big-all-img') as keyof typeof layoutMap
    const LayoutComponent = layoutMap[layout]

    const { data: initialPublications, isPending } = useQuery({
        ...getPublications({ category_id: currentNav.fk_category.id, skip: 0 }),
    })

    const [publications, setPublications] = useState<PublicationPublicPreview[]>([])
    const [visibleItemCount, setVisibleItemCount] = useState(100)

    useEffect(() => {
        setPublications([])
        setVisibleItemCount(100)
    }, [currentNav.fk_category.id])

    useEffect(() => {
        if (initialPublications?.data) {
            setPublications(initialPublications.data)
        }
    }, [initialPublications])

    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const handleLoadMore = () => {
        const newVisibleItemCount = visibleItemCount + 100
        setIsLoadingMore(true)

        // Get the next tags and add them to the displayedData
        queryClient
        .fetchQuery(
            getPublications({
                category_id: currentNav.fk_category.id,
                skip: visibleItemCount,
            }),
        )
        .then((newPubs) => {
            if (newPubs?.data) {
                setPublications((prevData) => [...prevData, ...newPubs.data])

                setVisibleItemCount(newVisibleItemCount)
            }
        }).finally(() => {
            setIsLoadingMore(false)
        })
    }

    return (
        <Container p={{ sm: 4, md: 0 }} h="full">
            <LayoutComponent
                publications={publications}
                isPending={isPending}
                category_img_path={currentNav.fk_media?.path ?? null}
                title={currentNav.title}
                handleLoadMore={handleLoadMore}
                isLoadingMore={isLoadingMore}
                showMore={publications.length !== initialPublications?.count}
            />
        </Container>
    )
}

export default CategoryPublications